import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type { UpdateObligationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateObligationDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { FrontendRouterInputs } from '../routerInputs';
import type { UpdateObligationInput } from '../scheduledEntityInputs';

type UpdateObligationTRPCInput = FrontendRouterInputs['obligation']['update'];

export const useUpdateObligation = createMutationHook({
  name: 'updateObligation',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.obligation.update.mutationOptions({ onSuccess }),
  mapInput: ({
    schedule,
    ...rest
  }: UpdateObligationInput): UpdateObligationTRPCInput => ({
    ...rest,
    ...mapScheduleToTRPC(schedule),
    Adherence: rest.Adherence ?? '',
  }),
  invalidate: (trpc) => [
    trpc.frontend.obligation.getById.queryKey(),
    trpc.frontend.obligation.register.queryKey(),
    trpc.frontend.obligation.obligationsByType.queryKey(),
    trpc.frontend.obligation.obligationList.queryKey(),
    // COI-889: linked-item reads embed the linked entity's title
    // (`linked-item.query.ts`), which this hook rewrites.
    trpc.frontend.linkedItem.linkedItems.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  // The tRPC update returns the full row; the legacy shape echoes the Id.
  mapResponse: (
    _result,
    input: UpdateObligationInput
  ): UpdateObligationMutation => ({
    updateChildObligation: {
      __typename: 'IdOutput',
      Id: input.Id,
    },
  }),
  graphqlDocument: UpdateObligationDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...input
  }: UpdateObligationInput) => ({ object: input }),
  evictFields: ['obligation'],
  refetchQueries: [namedOperations.Query.getObligationById],
});
